import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { Building } from '@map/shared';
import { speciesOf } from '../globe/ArabicLand.js';
import { healthColor } from '../styles/tokens.js';
import { fetchArabicWord } from './api.js';

const SPECIES_NAME: Record<ReturnType<typeof speciesOf>, string> = {
  palm: 'date palm',
  acacia: 'acacia',
  olive: 'olive',
  fig: 'fig',
  cedar: 'cedar',
  cypress: 'cypress',
  pomegranate: 'pomegranate',
};

/**
 * The Vocab country: every word mastered in Arabic, a tree each. Clicking a
 * tree opens its word here. Arabic is where words are practised; this only
 * reports them.
 */
export function ForestCard({ building, focusWordId = null }: { building: Building; focusWordId?: number | null }) {
  const words = building.forest?.words ?? [];
  const [openId, setOpenId] = useState<number | null>(focusWordId);
  const focused = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focusWordId === null) return;
    setOpenId(focusWordId);
    requestAnimationFrame(() => focused.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }, [focusWordId]);
  const wilting = words.filter((w) => !w.known || w.weathering >= 0.12).length;
  const newest = [...words].reverse();

  return (
    <div className="flex flex-col gap-3 text-sm">
      <header>
        <p className="text-base font-medium">Vocab</p>
        <p className="text-text-muted">
          {words.length === 0
            ? 'No words mastered yet.'
            : `${words.length} word${words.length === 1 ? '' : 's'} mastered${wilting ? ` · ${wilting} wilting` : ''}`}
        </p>
        <p className="mt-1 text-xs text-text-muted">
          A word is mastered once it has been used in ten accepted sentences across at least three of its forms.
        </p>
      </header>
      <ul className="flex flex-col">
        {newest.map((w) => {
          const open = openId === w.id;
          return (
            <li key={w.id} {...(w.id === focusWordId ? { ref: focused } : {})}>
              <button
                onClick={() => setOpenId(open ? null : w.id)}
                className="flex w-full items-center gap-3 rounded-[var(--radius-sm)] px-2 py-1.5 text-left hover:bg-surface-1"
                aria-expanded={open}
                style={w.id === focusWordId ? { background: 'var(--surface-1)', boxShadow: 'inset 2px 0 0 var(--accent)' } : undefined}
              >
                <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: healthColor(w.weathering) }} aria-hidden />
                <span dir="rtl" lang="ar" className="text-base">{w.lemma}</span>
                <span className="flex-1 truncate text-text-muted">{w.meaning}</span>
                <span className="shrink-0 text-xs text-text-muted">{SPECIES_NAME[speciesOf(w)]}</span>
              </button>
              {open && <WordDetails buildingId={building.id} wordId={w.id} />}
            </li>
          );
        })}
      </ul>
      <a className="text-xs underline" href="https://arabic.mappedself.com/review" target="_blank" rel="noreferrer">
        Review in Arabic
      </a>
    </div>
  );
}

function WordDetails({ buildingId, wordId }: { buildingId: string; wordId: number }) {
  const query = useQuery({
    queryKey: ['arabic-word', buildingId, wordId],
    queryFn: () => fetchArabicWord(buildingId, wordId),
    staleTime: 5 * 60_000,
  });
  if (query.isPending) return <p className="px-2 pb-2 text-xs text-text-muted">Loading…</p>;
  if (query.isError) return <p className="px-2 pb-2 text-xs text-text-muted">Could not load this word.</p>;
  const w = query.data.word;
  return (
    <div className="flex flex-col gap-2 px-2 pb-3 pt-1">
      <p className="text-xs text-text-muted">
        {w.root && <>root <span dir="rtl" lang="ar">{w.root}</span> · </>}
        mastered {w.masteredAt.slice(0, 10)}
        {!w.known && ' · forgotten since — answer it right again to revive it'}
      </p>
      <ol className="flex flex-col gap-1">
        {w.sentences.map((s, i) => (
          <li key={i} className="flex items-baseline justify-between gap-2">
            <span dir="rtl" lang="ar" className="text-base leading-relaxed">{s.text}</span>
            {s.form && s.form !== 'fixed' && <span className="shrink-0 text-[11px] text-text-muted">{s.form}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** The Comprehension country: recent reading, and the oasis it waters. */
export function ReadingCard({ building }: { building: Building }) {
  const r = building.reading;
  if (!r) return null;
  const percent = Math.round(r.score * 100);
  const last = r.lastReadAt ? r.lastReadAt.slice(0, 10) : null;
  return (
    <div className="flex flex-col gap-3 text-sm">
      <header>
        <p className="text-base font-medium">Comprehension</p>
        <p className="text-text-muted">
          {r.sittings === 0 ? 'Nothing read yet.' : `${r.sittings} sitting${r.sittings === 1 ? '' : 's'} read${last ? ` · last ${last}` : ''}`}
        </p>
      </header>
      <div>
        <div className="mb-1 flex justify-between text-xs text-text-muted">
          <span>Recent comprehension</span>
          <span className="tabular-nums">{percent}%{r.wpm ? ` · ${r.wpm} wpm` : ''}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-surface-1">
          <div className="h-full rounded-full" style={{ width: `${percent}%`, background: 'var(--accent)' }} />
        </div>
        <p className="mt-1 text-xs text-text-muted">Newest sittings count most. The oasis dries out after two days without reading.</p>
      </div>
      {r.recent.length > 0 && (
        <ul className="flex flex-col gap-1">
          {r.recent.map((s, i) => (
            <li key={i} className="flex justify-between gap-2 text-xs">
              <span className="truncate">{s.title}</span>
              <span className="shrink-0 tabular-nums text-text-muted">
                {Math.round(s.comprehension * 100)}%{s.wpm ? ` · ${Math.round(s.wpm)} wpm` : ''} · {s.at.slice(0, 10)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <a className="text-xs underline" href="https://arabic.mappedself.com/library" target="_blank" rel="noreferrer">
        Read in Arabic
      </a>
    </div>
  );
}
